import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataState } from '@delen/bd-data-states';
import { BdToast, BdToastManager, BdToastType } from '@delen/bd-toast';
import { extractTranslation } from '@delen/connect-core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { pipe, switchMap, tap } from 'rxjs';
import { AppRoute, AppRouteParam, AppRouteQueryParam } from 'src/app/app.route';
import { CONFIG } from 'src/app/core/config/config.token';
import { LoginRoute } from 'src/app/features/login/login.route';
import { withBdDataStates } from 'src/app/features/shared/bd-data-states';
import { ConfigEnv } from 'src/config/config.interface';
import { AuthenticationRepository } from './authentication.repository';
import { AuthenticationService } from './authentication.service';
import { TokenResponse } from './models/token-response';
import { TokenStorageService } from './token-storage.service';

export class AuthenticationState {
  shareId: string | null = null;
  relationId: string | null = null;
  relationPhone: string | null = null;
  authorizeDataState: DataState = DataState.Initial;
  accessToken: string | null = null;
  isExpired: boolean = false;
}

export const AuthenticationStore = signalStore(
  { providedIn: 'root' },
  withState(new AuthenticationState()),

  withBdDataStates(['relationPhone', 'accessToken']),

  withHooks((store) => {
    const tokenStorageService = inject(TokenStorageService);
    const authenticationService = inject(AuthenticationService);
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);

    return {
      onInit() {
        patchState(store, { accessToken: tokenStorageService.getAccessToken() });

        // Check token validity when window regains focus
        window.addEventListener('focus', () => {
          const isExpired = authenticationService.isAccessTokenExpired();

          if (isExpired) {
            const businessUnit = activatedRoute.root.firstChild?.snapshot.paramMap.get(AppRouteParam.BusinessUnit);
            const shareId = store.shareId();
            const relationId = store.relationId();

            // Only redirect if we have the necessary context
            if (!businessUnit || !shareId || !relationId) {
              return;
            }

            // Clear expired token
            tokenStorageService.clear();
            patchState(store, { accessToken: null });

            router.navigate(['/', businessUnit, AppRoute.Share, AppRoute.Login, LoginRoute.Verification], {
              queryParams: {
                [AppRouteQueryParam.ShareId]: shareId,
                [AppRouteQueryParam.RelationId]: relationId,
              },
            });
          }
        });
      },
    };
  }),

  withMethods(
    (
      store,
      authenticationService = inject(AuthenticationService),
      authenticationRepository = inject(AuthenticationRepository),
      tokenStorageService = inject(TokenStorageService),
      router = inject(Router),
      bdToastManager = inject(BdToastManager),
      translateService: TranslateService = inject(TranslateService),
      activatedRoute = inject(ActivatedRoute),
      config = inject(CONFIG),
    ) => ({
      setContext(shareId: string, relationId: string): void {
        patchState(store, { shareId, relationId });
      },

      getRelationPhone: rxMethod<{ shareId: string; relationId: string }>(
        pipe(
          tap(() => patchState(store, { relationPhoneDataState: DataState.Loading, isExpired: false })),
          switchMap(({ shareId, relationId }) => {
            const businessUnit =
              activatedRoute.root.firstChild?.snapshot.paramMap.get(AppRouteParam.BusinessUnit) ?? '';
            return authenticationRepository.getRelationPhone(shareId, relationId, businessUnit).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { relationPhone: response, relationPhoneDataState: DataState.None });
                },
                error: (error: HttpErrorResponse) => {
                  const isExpired = error.status === HttpStatusCode.Gone;
                  patchState(store, {
                    relationPhoneDataState: DataState.Error,
                    isExpired,
                  });
                },
              }),
            );
          }),
        ),
      ),

      authorize: rxMethod<{ shareId: string; relationId: string }>(
        pipe(
          tap(() => patchState(store, { authorizeDataState: DataState.Loading })),
          switchMap(({ shareId, relationId }) =>
            authenticationRepository
              .authorize({
                shareId,
                relationId,
                businessUnit: activatedRoute.root.firstChild?.snapshot.paramMap.get(AppRouteParam.BusinessUnit) ?? '',
              })
              .pipe(
                tapResponse({
                  next: (response) => {
                    const businessUnit = activatedRoute.root.firstChild?.snapshot.paramMap.get(
                      AppRouteParam.BusinessUnit,
                    );

                    patchState(store, { authorizeDataState: DataState.None });

                    // Show toast with code only in local or DVLP environment
                    if (
                      response?.code &&
                      (config.env === ConfigEnv.LOCAL || config.env === ConfigEnv.DVLP || isDevMode())
                    ) {
                      bdToastManager.open(
                        new BdToast({
                          title: 'Verification Code (Dev/DVLP Only)',
                          content: `Code: ${response.code}`,
                          type: BdToastType.Info,
                        }),
                      );
                    }

                    router.navigate(['/', businessUnit, AppRoute.Share, AppRoute.Login, LoginRoute.Confirmation], {
                      queryParamsHandling: 'preserve',
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, { authorizeDataState: DataState.Error });

                    if (error.status === HttpStatusCode.TooManyRequests) {
                      return bdToastManager.open(
                        new BdToast({
                          title: translateService.instant(extractTranslation('LOGIN_FAILED__TOO_MANY_REQUESTS__TITLE')),
                          content: translateService.instant(extractTranslation('LOGIN_FAILED__TOO_MANY_REQUESTS')),
                          type: BdToastType.Negative,
                        }),
                      );
                    }

                    bdToastManager.open(
                      new BdToast({
                        title: translateService.instant(extractTranslation('UNKNOWN_ERROR__TITLE')),
                        content: translateService.instant(extractTranslation('UNKNOWN_ERROR__TEXT')),
                        type: BdToastType.Negative,
                      }),
                    );
                  },
                }),
              ),
          ),
        ),
      ),

      getToken: rxMethod<{ shareId: string; relationId: string; code: string; onError: () => void }>(
        pipe(
          switchMap(({ shareId, relationId, code, onError }) =>
            authenticationRepository
              .getToken({
                shareId,
                relationId,
                code,
                businessUnit: activatedRoute.root.firstChild?.snapshot.paramMap.get(AppRouteParam.BusinessUnit) ?? '',
              })
              .pipe(
                tap(() => patchState(store, { accessTokenDataState: DataState.Loading })),
                tapResponse({
                  next: (response: TokenResponse) => {
                    const businessUnit = activatedRoute.root.firstChild?.snapshot.paramMap.get(
                      AppRouteParam.BusinessUnit,
                    );

                    patchState(store, { accessToken: response.accessToken, accessTokenDataState: DataState.None });
                    tokenStorageService.setAccessToken(response.accessToken);

                    router.navigate(['/', businessUnit, AppRoute.Share, AppRoute.Documents]);
                  },
                  error: (error) => {
                    patchState(store, { accessToken: null, accessTokenDataState: DataState.Error });
                    tokenStorageService.clear();
                    onError();

                    if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
                      return bdToastManager.open(
                        new BdToast({
                          content: translateService.instant(extractTranslation('LOGIN_FAILED__UNAUTHORIZED')),
                          type: BdToastType.Negative,
                        }),
                      );
                    }

                    return bdToastManager.open(
                      new BdToast({
                        title: translateService.instant(extractTranslation('UNKNOWN_ERROR__TITLE')),
                        content: translateService.instant(extractTranslation('UNKNOWN_ERROR__TEXT')),
                        type: BdToastType.Negative,
                      }),
                    );
                  },
                }),
              ),
          ),
        ),
      ),

      logout(): void {
        const token = authenticationService.getAccessToken()!;
        const businessUnit = activatedRoute.root.firstChild?.snapshot.paramMap.get(AppRouteParam.BusinessUnit);

        // Clear token from state and storage (but keep shareId/relationId for redirect)
        tokenStorageService.clear();

        router.navigate(['/', businessUnit, AppRoute.Share, AppRoute.Login, LoginRoute.Verification], {
          queryParams: {
            [AppRouteQueryParam.ShareId]: token.shareId,
            [AppRouteQueryParam.RelationId]: token.sub,
          },
        });
      },
    }),
  ),
);
